import React from 'react';
import Modal from './Model';
import { CalendarDays, CalendarRange, X } from 'lucide-react';

interface ScopeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectScope: (scope: 'single' | 'future') => void;
  action: 'edit' | 'delete';
}

const ScopeModal: React.FC<ScopeModalProps> = ({ isOpen, onClose, onSelectScope, action }) => {
  if (!isOpen) return null;

  const title = action === 'edit' ? 'Edit Recurring Event' : 'Delete Recurring Event';

  return (
    <Modal isOpen={isOpen} onClose={onClose} >
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 max-w-[500px] mx-auto overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <h2 className="text-xl font-bold text-gray-900 tracking-tight">{title}</h2>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-white rounded-xl text-gray-400 hover:text-[#7E57C2] shadow-sm transition-all duration-200 border border-transparent hover:border-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-8 space-y-6">
          <p className="text-sm font-medium text-gray-600">
            Please select how you would like this change to apply to your recurring event series.
          </p>

          <div className="flex flex-col gap-4">
            <button
              onClick={() => onSelectScope('single')}
              className="flex items-start gap-4 p-4 rounded-xl border border-gray-200 hover:border-[#7E57C2] hover:bg-purple-50/50 transition-all duration-200 text-left group"
            >
              <div className="mt-1 p-2 bg-gray-100 rounded-lg group-hover:bg-purple-100 group-hover:text-[#7E57C2] text-gray-500 transition-colors">
                <CalendarDays size={18} />
              </div>
              <div>
                <strong className="block font-bold text-gray-900 group-hover:text-[#7E57C2] transition-colors">This event only</strong>
                <span className="text-xs font-medium text-gray-500">Only this specific instance will be affected.</span>
              </div>
            </button>

            <button
              onClick={() => onSelectScope('future')}
              className="flex items-start gap-4 p-4 rounded-xl border border-gray-200 hover:border-[#7E57C2] hover:bg-purple-50/50 transition-all duration-200 text-left group"
            >
              <div className="mt-1 p-2 bg-gray-100 rounded-lg group-hover:bg-purple-100 group-hover:text-[#7E57C2] text-gray-500 transition-colors">
                <CalendarRange size={18} />
              </div>
              <div>
                <strong className="block font-bold text-gray-900 group-hover:text-[#7E57C2] transition-colors">This and following events</strong>
                <span className="text-xs font-medium text-gray-500">This and all subsequent instances in the series will be affected.</span>
              </div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-gray-100 bg-gray-50/50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-white border border-gray-200 text-gray-600 font-bold rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-sm"
          >
            Cancel
          </button>
        </div>
        
      </div>
    </Modal>
  );
};

export default ScopeModal;